import { useCallback, useEffect, useRef, useState } from 'react';
import { centerShift, Glide, glideToward } from './followModel';

/** The attribute a scenario card carries its id in, so the page can find the one it follows. */
export const FOLLOW_ATTRIBUTE = 'data-follow-id';

/**
 * The class of a timeline row that is not finished yet: a part of a document's way still to come or being worked on,
 * the step the scenario is doing, or the now line.
 */
export const OPEN_ROW = 'sf-row--open';

// The head of a scenario's card, held while its timeline has nothing open yet.
const CARD_HEAD = '.rd-scn__head';

// Keys that scroll the page; pressing one means the person wants to look somewhere else.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

// A frame that comes late, after a tab was in the background, should not turn into one big leap.
const MAX_FRAME_MS = 100;

const isTyping = (target: EventTarget | null) => {
    const element = target as HTMLElement | null;

    return (
        !!element &&
        (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(element.tagName))
    );
};

/**
 * Keeps what the run is busy with in the middle of the screen, gliding along as it moves on: the topmost line of the
 * topmost running scenario that is not finished yet - a part of a document's way still to come or being worked on,
 * the step it is doing, or else the now line - or the scenario's head while its timeline has none. Lines that come
 * in above it push the page along, so the line stays where it is.
 *
 * Following stops as soon as the person scrolls themselves - wheel, touch, keys or the scrollbar - and only comes
 * back through {@link follow}. Scroll events cannot tell the two apart, because the page's own scrolling fires them
 * too, so the hook listens to what a person does rather than to what the page does.
 *
 * @param targetId The scenario to follow, or undefined while none is running.
 * @param active Whether there is anything to follow at all: false once the run has finished.
 * @param topOffset What covers the top of the viewport, read every frame.
 */
export const useFollowRun = ({
    targetId,
    active,
    topOffset,
}: {
    targetId?: string;
    active: boolean;
    topOffset: () => number;
}) => {
    const [following, setFollowing] = useState(true);
    const offset = useRef(topOffset);

    offset.current = topOffset;

    useEffect(() => {
        if (!following || !active) {
            return;
        }

        const stop = () => setFollowing(false);
        const onKey = (event: KeyboardEvent) => {
            if (SCROLL_KEYS.has(event.key) && !isTyping(event.target)) stop();
        };
        // A press right of the content is a press on the page's scrollbar.
        const onPointer = (event: PointerEvent) => {
            if (event.clientX >= document.documentElement.clientWidth) stop();
        };

        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('touchmove', stop, { passive: true });
        window.addEventListener('keydown', onKey);
        window.addEventListener('pointerdown', onPointer);

        return () => {
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchmove', stop);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('pointerdown', onPointer);
        };
    }, [following, active]);

    useEffect(() => {
        if (!following || !active || !targetId) {
            return;
        }

        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        const selector = `[${FOLLOW_ATTRIBUTE}="${CSS.escape(targetId)}"]`;
        const root = document.documentElement;
        // Instant, because Bootstrap makes the page scroll smoothly and that would fight every frame.
        const scrollTo = (top: number) => window.scrollTo({ top, behavior: 'instant' as ScrollBehavior });
        let frame = 0;
        let last = performance.now();
        let glide: Glide = { position: window.scrollY, velocity: 0 };
        // What the page holds in place, and where it stood after the last frame.
        let anchor: HTMLElement | undefined;
        let anchorTop = 0;

        // The hook keeps the anchor in place itself; the browser's own scroll anchoring would pick another element.
        const anchoring = root.style.overflowAnchor;

        root.style.overflowAnchor = 'none';

        const tick = (moment: number) => {
            const elapsed = Math.min(moment - last, MAX_FRAME_MS);
            const card = document.querySelector<HTMLElement>(selector);

            last = moment;

            if (card) {
                const topOffset = offset.current();
                const held =
                    card.querySelector<HTMLElement>(`.${OPEN_ROW}`) ??
                    card.querySelector<HTMLElement>(CARD_HEAD) ??
                    card;

                // Whatever moved the held line since the last frame was not the page scrolling - a card above that
                // folded, a row put in above it - so the page moves along with it before anything is painted.
                if (held === anchor) {
                    const shift = held.getBoundingClientRect().top - anchorTop;

                    if (Math.abs(shift) >= 0.5) {
                        glide = { ...glide, position: glide.position + shift };
                        scrollTo(glide.position);
                    }
                }

                // The browser clamps and rounds; carry on from where the page really is when that differs.
                if (Math.abs(window.scrollY - glide.position) > 1) {
                    glide = { ...glide, position: window.scrollY };
                }

                const box = held.getBoundingClientRect();
                const maxScroll = Math.max(root.scrollHeight - window.innerHeight, 0);
                const distance = centerShift(box.top, box.height, topOffset, window.innerHeight);
                const desired = Math.min(Math.max(window.scrollY + distance, 0), maxScroll);

                glide = reduced ? { position: desired, velocity: 0 } : glideToward(glide, desired, elapsed);

                if (Math.abs(glide.position - window.scrollY) >= 0.5) {
                    scrollTo(glide.position);
                }

                anchor = held;
                anchorTop = held.getBoundingClientRect().top;
            }

            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(frame);
            root.style.overflowAnchor = anchoring;
        };
    }, [following, active, targetId]);

    const follow = useCallback(() => setFollowing(true), []);

    return { following, follow };
};
